/**
 * Outbox v2 — the backend routes whose 200 is NOT the verdict (ATL-1445 stage 3, P3; review 1 B1).
 *
 * A handful of bulk routes isolate failures per item: they answer 200 with `results[]`, and an item
 * that did not land says so only in its own row. v1 learned this on `/products/stock/bulk` (ATL-962:
 * "the replay did not throw" removed entries whose every item had failed). The first version of P3
 * checked that one route and read a partial failure on `/products/cost-cascade/bulk` — composite
 * COGS — as delivered, i.e. removed the only copy of the write.
 *
 * So every such route is listed here with the predicate that finds a failed item, and a test pins
 * the list against the BACKEND: every controller route whose response DTO carries a `results` array
 * must be here, and nothing else may be (`outboxPerItemResults.test.js`). A new per-item route cannot
 * slip through as "200 = delivered".
 *
 * A response from one of these routes WITHOUT a `results` array is not evidence of success either:
 * the contract says the rows are the verdict, and a body without them cannot say which items landed.
 * It is held (`unverifiable`), never assumed delivered.
 */

export interface PerItemResultRoute {
  method: 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  /** Path relative to the API base, as the outbox stores it. */
  path: string
  /** The backend response DTO class that carries `results[]` (pinned against the controller). */
  responseDto: string
  /** Is this result row a failure? Rows are data, so anything unrecognised counts as a failure. */
  failed: (row: unknown) => boolean
  /** Why it is here, and whether the app ever sends it. */
  note: string
}

const okFalse = (row: unknown): boolean =>
  !row || typeof row !== 'object' || (row as { ok?: unknown }).ok !== true

const HEAL_SUCCESS: ReadonlySet<string> = new Set(['healed', 'already-present', 'soft-deleted'])

export const PER_ITEM_RESULT_ROUTES: readonly PerItemResultRoute[] = [
  {
    method: 'POST',
    path: '/products/stock/bulk',
    responseDto: 'BulkStockUpdateResponseDto',
    failed: okFalse,
    note: 'absolute stock mirror (products.updateStock); ATL-962',
  },
  {
    method: 'POST',
    path: '/products/cost-cascade/bulk',
    responseDto: 'BulkCostCascadeResponseDto',
    failed: okFalse,
    note: 'composite cost cascade (products.costCascade, updateProduct); review 1 B1',
  },
  {
    method: 'POST',
    path: '/products/store-settings/seed',
    responseDto: 'SeedStoreProductSettingsResponseDto',
    failed: okFalse,
    note: 'new-store price/visibility seed (saveNewStore); ATL-790',
  },
  {
    method: 'POST',
    path: '/products/heal-missing',
    responseDto: 'HealMissingProductsResponseDto',
    failed: (row) =>
      !row || typeof row !== 'object' || !HEAL_SUCCESS.has(String((row as { status?: unknown }).status)),
    note: 'operator-only (MigrationTokenGuard); the app never queues it, listed so the pin is complete',
  },
]

export function perItemResultRouteOf(request: { method?: string; url?: string }): PerItemResultRoute | null {
  return PER_ITEM_RESULT_ROUTES.find((r) => r.method === request.method && r.path === request.url) ?? null
}

/**
 * `null` when the response is a complete success; otherwise how many rows failed, or `'unverifiable'`
 * when the body carries no `results` array at all.
 */
export function perItemFailures(route: PerItemResultRoute, response: unknown): number | 'unverifiable' | null {
  const results = (response as { results?: unknown } | null | undefined)?.results
  if (!Array.isArray(results)) return 'unverifiable'
  const failed = results.filter((row) => route.failed(row)).length
  return failed === 0 ? null : failed
}
